import {
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloudUploadOutlined,
  CopyOutlined,
  DeleteOutlined,
  DesktopOutlined,
  DownOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  HolderOutlined,
  LoadingOutlined,
  MobileOutlined,
  MoreOutlined,
  PlusOutlined,
  RedoOutlined,
  UndoOutlined,
  UpOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { Alert, Dropdown, Drawer, Empty, Segmented, Skeleton, Tag, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Sortable from 'sortablejs'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  changeGraphicPageStatus,
  createGraphicBlock,
  createGraphicModule,
  deleteGraphicBlock,
  deleteGraphicModule,
  duplicateGraphicModule,
  getGraphicPageDraftPreview,
  getGraphicPageTree,
  reorderGraphicBlocks,
  reorderGraphicModules,
  siteAssetVariantUrl,
  updateGraphicBlock,
  updateGraphicModule,
  updateGraphicPageCover,
  updateGraphicPageMeta,
} from '../../../api'
import type { SaasApiError } from '../../../api'
import { ImageAssetPicker, ImageAssetPickerField } from '../../../components/ImageAssetPicker'
import { VistoeraActionConfirm } from '../../../ui/action'
import { EntryPickerModal } from '../../../components/EntryPicker'
import type { ContentEntry } from '../../../types'
import {
  GRAPHIC_BLOCK_TYPE_GROUPS,
  GRAPHIC_BLOCK_TYPE_LABELS,
  GRAPHIC_PAGE_STATUS_COLORS,
  GRAPHIC_PAGE_STATUS_LABELS,
  GRAPHIC_PAGE_TYPE_LABELS,
  type GraphicBlockType,
  type GraphicPageBlock,
  type GraphicPageModule,
  type GraphicPageStatus,
  type GraphicPageTree,
} from '../../../graphicPageTypes'
import BlockEditPanel from './BlockEditPanel'
import MediaUrlCreateDialog from './MediaUrlCreateDialog'
import { DEFAULT_CROP_DESKTOP, DEFAULT_CROP_MOBILE } from './CropEditor'
import GraphicTreeRenderer, { type ImageResolver } from './GraphicTreeRenderer'
import VisualCropEditor from './VisualCropEditor'
import './graphic-editor.css'

type HistoryAction = { label: string; undo: () => Promise<void>; redo: () => Promise<void> }
type SaveState = 'idle' | 'saving' | 'saved' | 'error'

function blockSummary(block: GraphicPageBlock): string {
  const c = block.content || {}
  switch (block.block_type) {
    case 'heading':
      return String(c.text || '（空标题）')
    case 'text':
      return String(c.html || '').replace(/<[^>]+>/g, '').slice(0, 60) || '（空正文）'
    case 'quote':
      return String(c.text || '（空引用）')
    case 'image':
      return block.asset_id ? String(c.caption || '已选择图片') : '未选择图片'
    case 'hero':
      return String(c.title || '（未设置标题）')
    case 'collection_item':
      return block.linked_entry?.label || '未关联条目'
    case 'exhibition_ref':
    case 'research_ref':
      return block.linked_entry?.label || '未关联内容'
    case 'featured_collection':
    case 'featured_exhibition':
    case 'featured_research':
      return `已选择 ${block.resolved_items?.length ?? 0} 项`
    case 'video':
    case 'audio':
      return String(c.title || c.external_url || '未设置')
    default:
      return ''
  }
}

/**
 * 图文编辑 Editor Shell — functional parity first (spec §6): 顶部操作栏 /
 * 左侧封面+模块列表 / 右侧基本信息+模块编辑, ported from LiangCollection's
 * `publication_editor.html` + `content_editor.js` structure (see audit
 * doc). Every mutating action below both (a) calls the real authenticated
 * API so state survives refresh, and (b) pushes a real do/undo pair onto
 * `undoStackRef` — same "restore/delete API call" undo mechanism the
 * audit found in LiangCollection's own `pushUndo`/`content_editor.js`
 * (new UUIDs on restore are an accepted, documented difference).
 */
const IMAGE_PICKER_BLOCK_TYPES: GraphicBlockType[] = ['image', 'hero']

const ENTRY_PICKER_BLOCK_TYPES: Partial<Record<GraphicBlockType, 'item' | 'exhibition' | 'research'>> = {
  collection_item: 'item',
  exhibition_ref: 'exhibition',
  research_ref: 'research',
  featured_collection: 'item',
  featured_exhibition: 'exhibition',
  featured_research: 'research',
}

const FEATURED_BLOCK_TYPES = new Set<GraphicBlockType>(['featured_collection', 'featured_exhibition', 'featured_research'])

const MEDIA_CONFIGURE_BLOCK_TYPES = new Set<GraphicBlockType>(['video', 'audio'])

const CREATE_IMMEDIATELY_BLOCK_TYPES = new Set<GraphicBlockType>(['heading', 'text', 'quote', 'divider', 'spacer'])

type PendingBlockInsert =
  | { kind: 'image'; moduleId: string; blockType: 'image' | 'hero'; afterBlockId?: string }
  | {
      kind: 'entry'
      moduleId: string
      blockType: GraphicBlockType
      contentType: 'item' | 'exhibition' | 'research'
      afterBlockId?: string
      multiple: boolean
    }
  | { kind: 'media'; moduleId: string; blockType: 'video' | 'audio'; afterBlockId?: string }

type BlockCreateOpts = {
  assetId?: string | null
  linkedEntryId?: string | null
  contentPatch?: Record<string, unknown>
}

function entryPickerTitle(contentType: 'item' | 'exhibition' | 'research', multiple: boolean): string {
  if (multiple) {
    return contentType === 'item' ? '选择精选条目' : contentType === 'exhibition' ? '选择精选专题' : '选择精选文章'
  }
  return contentType === 'item' ? '选择条目' : contentType === 'exhibition' ? '选择专题' : '选择文章'
}

export default function GraphicPageEditorPage({ workspaceId, siteId, siteBase }: { workspaceId: string; siteId: string; siteBase: string }) {
  const { pageId = '' } = useParams()
  const navigate = useNavigate()
  const [search] = useSearchParams()
  const { t } = useTranslation(['website', 'actions'])

  const [tree, setTree] = useState<GraphicPageTree | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null)
  // UI_V2 round: the main column now renders ALL modules as independent
  // collapsible cards (LiangCollection's original "all sections on one
  // page" layout — audit doc; previously Vistoera only showed the single
  // `activeModuleId` module, which is what made "insert a block into
  // module 3 while looking at module 1" impossible, spec §六/七/八).
  // `expandedModuleIds` tracks which module cards are open; several can
  // be open at once, same as any accordion-style CMS outline.
  const [expandedModuleIds, setExpandedModuleIds] = useState<Set<string>>(new Set())
  const [basicInfoCollapsed, setBasicInfoCollapsed] = useState(false)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null)
  const [busy, setBusy] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(search.get('preview') === '1')
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop')
  const [previewTree, setPreviewTree] = useState<GraphicPageTree | null>(null)
  const [historyVersion, setHistoryVersion] = useState(0)
  const [pendingInsert, setPendingInsert] = useState<PendingBlockInsert | null>(null)

  const undoStackRef = useRef<HistoryAction[]>([])
  const redoStackRef = useRef<HistoryAction[]>([])
  const blockRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const moduleCardRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const expandModule = useCallback((moduleId: string) => {
    setExpandedModuleIds((prev) => (prev.has(moduleId) ? prev : new Set(prev).add(moduleId)))
  }, [])
  const toggleModuleExpanded = (moduleId: string) => {
    setExpandedModuleIds((prev) => {
      const next = new Set(prev)
      if (next.has(moduleId)) next.delete(moduleId)
      else next.add(moduleId)
      return next
    })
  }
  const scrollToModule = (moduleId: string) => {
    expandModule(moduleId)
    setTimeout(() => moduleCardRefs.current[moduleId]?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
  }
  const scrollToBlock = (moduleId: string, blockId: string) => {
    expandModule(moduleId)
    setTimeout(() => blockRefs.current[blockId]?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 90)
  }

  // Pending debounced edits (title/module text/block content, all fired
  // ~700-900ms after the last keystroke) + in-flight API calls both count
  // as "dirty" for the `beforeunload` guard below (spec §31/§13 — LiangCollection's
  // autosave/unload-protection semantics: never let a tab close silently
  // drop keystrokes that haven't reached the server yet).
  const pendingSaves = useRef(0)
  const pendingDebounceKeys = useRef<Set<string>>(new Set())
  const markDebouncePending = (key: string) => pendingDebounceKeys.current.add(key)
  const clearDebouncePending = (key: string) => pendingDebounceKeys.current.delete(key)
  const beginSave = () => {
    pendingSaves.current += 1
    setSaveState('saving')
  }
  const endSave = (ok: boolean) => {
    pendingSaves.current = Math.max(0, pendingSaves.current - 1)
    if (!ok) {
      setSaveState('error')
      return
    }
    if (pendingSaves.current === 0) {
      setSaveState('saved')
      setLastSavedAt(new Date())
    }
  }

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (pendingSaves.current > 0 || pendingDebounceKeys.current.size > 0 || saveState === 'error') {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [saveState])

  const refreshTree = useCallback(async () => {
    const { data } = await getGraphicPageTree(workspaceId, siteId, pageId)
    setTree(data)
    return data
  }, [workspaceId, siteId, pageId])

  useEffect(() => {
    setError(null)
    getGraphicPageTree(workspaceId, siteId, pageId)
      .then((r) => {
        setTree(r.data)
        if (r.data.modules.length > 0) {
          setActiveModuleId(r.data.modules[0].id)
          setExpandedModuleIds(new Set([r.data.modules[0].id]))
        }
      })
      .catch((err) => setError((err as SaasApiError).message))
  }, [workspaceId, siteId, pageId])

  const pushHistory = (action: HistoryAction) => {
    undoStackRef.current.push(action)
    if (undoStackRef.current.length > 30) undoStackRef.current.shift()
    redoStackRef.current = []
    setHistoryVersion((v) => v + 1)
  }

  const onUndo = async () => {
    const action = undoStackRef.current.pop()
    if (!action) return
    beginSave()
    try {
      await action.undo()
      redoStackRef.current.push(action)
    } catch (err) {
      message.error((err as SaasApiError).message || '撤销失败')
    } finally {
      endSave(true)
      setHistoryVersion((v) => v + 1)
    }
  }
  const onRedo = async () => {
    const action = redoStackRef.current.pop()
    if (!action) return
    beginSave()
    try {
      await action.redo()
      undoStackRef.current.push(action)
    } catch (err) {
      message.error((err as SaasApiError).message || '重做失败')
    } finally {
      endSave(true)
      setHistoryVersion((v) => v + 1)
    }
  }

  // -------------------------------------------------------------- Page meta
  const page = tree?.page
  const metaFieldTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const metaFieldPriorValue = useRef<Record<string, string>>({})

  const commitPageMeta = async (field: 'title' | 'subtitle' | 'summary' | 'author', value: string) => {
    beginSave()
    try {
      await updateGraphicPageMeta(workspaceId, siteId, pageId, { [field]: value })
      await refreshTree()
      endSave(true)
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message || '保存失败')
    }
  }

  const onMetaFieldChange = (field: 'title' | 'subtitle' | 'summary' | 'author', value: string) => {
    if (!(field in metaFieldPriorValue.current)) {
      metaFieldPriorValue.current[field] = (page?.[field] as string) || ''
    }
    setTree((prev) => (prev ? { ...prev, page: { ...prev.page, [field]: value } } : prev))
    clearTimeout(metaFieldTimers.current[field])
    markDebouncePending(`meta:${field}`)
    metaFieldTimers.current[field] = setTimeout(async () => {
      clearDebouncePending(`meta:${field}`)
      const prior = metaFieldPriorValue.current[field]
      delete metaFieldPriorValue.current[field]
      await commitPageMeta(field, value)
      if (prior !== value) {
        pushHistory({
          label: `编辑${field}`,
          undo: async () => {
            await commitPageMeta(field, prior)
          },
          redo: async () => {
            await commitPageMeta(field, value)
          },
        })
      }
    }, 900)
  }

  const onToggleFrontendVisible = async (checked: boolean) => {
    beginSave()
    try {
      await updateGraphicPageMeta(workspaceId, siteId, pageId, { frontend_visible: checked })
      await refreshTree()
      endSave(true)
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
    }
  }

  // ------------------------------------------------------------------ Cover
  const onCoverAssetChange = async (assetId: string | null) => {
    beginSave()
    try {
      await updateGraphicPageCover(workspaceId, siteId, pageId, { cover_asset_id: assetId, cover_source_type: assetId ? 'upload' : 'auto' })
      await refreshTree()
      endSave(true)
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
    }
  }
  const onCoverCropChange = async (which: 'cover_crop_desktop' | 'cover_crop_mobile', value: unknown) => {
    beginSave()
    try {
      await updateGraphicPageCover(workspaceId, siteId, pageId, { [which]: value } as never)
      await refreshTree()
      endSave(true)
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
    }
  }

  // --------------------------------------------------------------- Modules
  const addModule = async () => {
    const holder = { id: '' }
    const doCreate = async () => {
      const { data } = await createGraphicModule(workspaceId, siteId, pageId, {})
      holder.id = data.id
      await refreshTree()
      setActiveModuleId(data.id)
      scrollToModule(data.id)
    }
    beginSave()
    try {
      await doCreate()
      endSave(true)
      pushHistory({
        label: '新增模块',
        undo: async () => {
          await deleteGraphicModule(workspaceId, siteId, pageId, holder.id)
          await refreshTree()
        },
        redo: doCreate,
      })
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
    }
  }

  // VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §13/§16 — icon-triggered lightweight
  // delete confirmation, migrated from centered `Modal.confirm` to the
  // anchored `VistoeraActionConfirm` (wrapped around the trigger button in
  // JSX below). This function only performs the mutation + undo/redo history
  // wiring; the confirmation UI/copy lives in the wrapping component.
  const deleteModule = async (module: GraphicPageModule) => {
    const snapshot = module
    const holder = { id: module.id }
    const doDelete = async () => {
      await deleteGraphicModule(workspaceId, siteId, pageId, holder.id)
      const t = await refreshTree()
      if (activeModuleId === holder.id) setActiveModuleId(t.modules[0]?.id ?? null)
    }
    beginSave()
    try {
      await doDelete()
      endSave(true)
      pushHistory({
        label: '删除模块',
        undo: async () => {
          const { data: recreated } = await createGraphicModule(workspaceId, siteId, pageId, { title: snapshot.title || undefined })
          holder.id = recreated.id
          await updateGraphicModule(workspaceId, siteId, pageId, recreated.id, {
            subtitle: snapshot.subtitle,
            intro: snapshot.intro,
            is_visible: snapshot.is_visible,
            show_in_toc: snapshot.show_in_toc,
          })
          for (const b of snapshot.blocks) {
            const { data: nb } = await createGraphicBlock(workspaceId, siteId, pageId, recreated.id, { block_type: b.block_type })
            await updateGraphicBlock(workspaceId, siteId, pageId, nb.id, {
              content: b.content,
              is_visible: b.is_visible,
              asset_id: b.asset_id,
              linked_entry_id: b.linked_entry_id,
            })
          }
          await refreshTree()
          setActiveModuleId(holder.id)
        },
        redo: doDelete,
      })
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
      throw err
    }
  }

  const duplicateModuleAction = async (module: GraphicPageModule) => {
    const holder = { id: '' }
    const doDuplicate = async () => {
      const { data } = await duplicateGraphicModule(workspaceId, siteId, pageId, module.id)
      holder.id = data.id
      await refreshTree()
      setActiveModuleId(data.id)
      scrollToModule(data.id)
    }
    beginSave()
    try {
      await doDuplicate()
      endSave(true)
      pushHistory({
        label: '复制模块',
        undo: async () => {
          await deleteGraphicModule(workspaceId, siteId, pageId, holder.id)
          await refreshTree()
        },
        redo: doDuplicate,
      })
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
    }
  }

  const toggleModuleField = async (module: GraphicPageModule, field: 'is_visible' | 'show_in_toc', value: boolean) => {
    const prior = module[field]
    const apply = async (v: boolean) => {
      beginSave()
      try {
        await updateGraphicModule(workspaceId, siteId, pageId, module.id, { [field]: v })
        await refreshTree()
        endSave(true)
      } catch (err) {
        endSave(false)
        message.error((err as SaasApiError).message)
      }
    }
    await apply(value)
    pushHistory({ label: field, undo: () => apply(prior), redo: () => apply(value) })
  }

  const moduleTextTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const moduleTextPrior = useRef<Record<string, string>>({})
  const onModuleTextChange = (module: GraphicPageModule, field: 'title' | 'subtitle' | 'intro', value: string) => {
    const key = `${module.id}:${field}`
    if (!(key in moduleTextPrior.current)) moduleTextPrior.current[key] = module[field]
    setTree((prev) =>
      prev ? { ...prev, modules: prev.modules.map((m) => (m.id === module.id ? { ...m, [field]: value } : m)) } : prev,
    )
    clearTimeout(moduleTextTimers.current[key])
    markDebouncePending(`module:${key}`)
    moduleTextTimers.current[key] = setTimeout(async () => {
      clearDebouncePending(`module:${key}`)
      const prior = moduleTextPrior.current[key]
      delete moduleTextPrior.current[key]
      const apply = async (v: string) => {
        beginSave()
        try {
          await updateGraphicModule(workspaceId, siteId, pageId, module.id, { [field]: v })
          await refreshTree()
          endSave(true)
        } catch (err) {
          endSave(false)
          message.error((err as SaasApiError).message)
        }
      }
      await apply(value)
      if (prior !== value) pushHistory({ label: `编辑模块${field}`, undo: () => apply(prior), redo: () => apply(value) })
    }, 900)
  }

  const applyModuleOrder = async (order: string[]) => {
    beginSave()
    try {
      await reorderGraphicModules(workspaceId, siteId, pageId, order)
      await refreshTree()
      endSave(true)
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
    }
  }
  const commitModuleReorder = async (nextOrder: string[], priorOrder: string[]) => {
    await applyModuleOrder(nextOrder)
    pushHistory({ label: '排序模块', undo: () => applyModuleOrder(priorOrder), redo: () => applyModuleOrder(nextOrder) })
  }

  const moveModule = async (module: GraphicPageModule, direction: -1 | 1) => {
    if (!tree) return
    const ordered = tree.modules.slice().sort((a, b) => a.sort_order - b.sort_order)
    const idx = ordered.findIndex((m) => m.id === module.id)
    const swapWith = idx + direction
    if (swapWith < 0 || swapWith >= ordered.length) return
    const priorOrder = ordered.map((m) => m.id)
    const nextOrder = ordered.map((m) => m.id)
    ;[nextOrder[idx], nextOrder[swapWith]] = [nextOrder[swapWith], nextOrder[idx]]
    await commitModuleReorder(nextOrder, priorOrder)
  }

  // Sidebar module list drag/sort — reuses the SortableJS library
  // LiangCollection's own outline used (audit doc §36); Sortable mutates
  // the DOM directly on drag, then `onEnd` recomputes the id order from
  // `moduleOrderRef` (always kept fresh below) and pushes it through the
  // exact same authenticated reorder API + undo/redo path as the ↑/↓
  // buttons, so drag and buttons are just two input methods for one action.
  const moduleListElRef = useRef<HTMLDivElement | null>(null)
  const moduleOrderRef = useRef<string[]>([])
  useEffect(() => {
    const el = moduleListElRef.current
    if (!el) return
    const sortable = Sortable.create(el, {
      handle: '.ge-drag-handle',
      animation: 150,
      onEnd: (evt) => {
        if (evt.oldIndex == null || evt.newIndex == null || evt.oldIndex === evt.newIndex) return
        const priorOrder = moduleOrderRef.current.slice()
        const nextOrder = priorOrder.slice()
        const [moved] = nextOrder.splice(evt.oldIndex, 1)
        nextOrder.splice(evt.newIndex, 0, moved)
        void commitModuleReorder(nextOrder, priorOrder)
      },
    })
    return () => sortable.destroy()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const focusNewBlockEditor = (blockId: string) => {
    setTimeout(() => {
      const root = blockRefs.current[blockId]
      const focusable = root?.querySelector('input, textarea, .ql-editor') as HTMLElement | null
      focusable?.focus()
    }, 120)
  }

  const addBlock = async (moduleId: string, blockType: GraphicBlockType, afterBlockId?: string, opts?: BlockCreateOpts) => {
    const holder = { id: '' }
    const doCreate = async () => {
      const { data } = await createGraphicBlock(workspaceId, siteId, pageId, moduleId, { block_type: blockType, after_block_id: afterBlockId })
      holder.id = data.id
      const patch: {
        asset_id?: string | null
        linked_entry_id?: string | null
        content?: Record<string, unknown>
      } = {}
      if (opts?.assetId) patch.asset_id = opts.assetId
      if (opts?.linkedEntryId) patch.linked_entry_id = opts.linkedEntryId
      if (opts?.contentPatch && Object.keys(opts.contentPatch).length > 0) patch.content = opts.contentPatch
      if (Object.keys(patch).length > 0) {
        await updateGraphicBlock(workspaceId, siteId, pageId, data.id, patch)
      }
      await refreshTree()
      scrollToBlock(moduleId, data.id)
      if (CREATE_IMMEDIATELY_BLOCK_TYPES.has(blockType) && blockType !== 'divider' && blockType !== 'spacer') {
        focusNewBlockEditor(data.id)
      }
    }
    beginSave()
    try {
      await doCreate()
      endSave(true)
      pushHistory({
        label: '新增内容块',
        undo: async () => {
          await deleteGraphicBlock(workspaceId, siteId, pageId, holder.id)
          await refreshTree()
        },
        redo: doCreate,
      })
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
    }
  }

  const addImageBlocks = async (
    moduleId: string,
    blockType: 'image' | 'hero',
    afterBlockId: string | undefined,
    assetIds: string[],
  ) => {
    if (assetIds.length === 0) return
    const createdBlockIds: string[] = []
    const doCreate = async () => {
      createdBlockIds.length = 0
      let anchorAfter = afterBlockId
      for (const assetId of assetIds) {
        const { data } = await createGraphicBlock(workspaceId, siteId, pageId, moduleId, {
          block_type: blockType,
          after_block_id: anchorAfter,
        })
        createdBlockIds.push(data.id)
        await updateGraphicBlock(workspaceId, siteId, pageId, data.id, { asset_id: assetId })
        anchorAfter = data.id
      }
      await refreshTree()
      scrollToBlock(moduleId, createdBlockIds[0])
    }
    beginSave()
    try {
      await doCreate()
      endSave(true)
      pushHistory({
        label: assetIds.length > 1 ? `插入 ${assetIds.length} 张图片` : '新增内容块',
        undo: async () => {
          for (const blockId of [...createdBlockIds].reverse()) {
            await deleteGraphicBlock(workspaceId, siteId, pageId, blockId)
          }
          await refreshTree()
        },
        redo: doCreate,
      })
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
    }
  }

  const requestAddBlock = (moduleId: string, blockType: GraphicBlockType, afterBlockId?: string) => {
    if (IMAGE_PICKER_BLOCK_TYPES.includes(blockType)) {
      setPendingInsert({ kind: 'image', moduleId, blockType: blockType as 'image' | 'hero', afterBlockId })
      return
    }
    const entryContentType = ENTRY_PICKER_BLOCK_TYPES[blockType]
    if (entryContentType) {
      setPendingInsert({
        kind: 'entry',
        moduleId,
        blockType,
        contentType: entryContentType,
        afterBlockId,
        multiple: FEATURED_BLOCK_TYPES.has(blockType),
      })
      return
    }
    if (MEDIA_CONFIGURE_BLOCK_TYPES.has(blockType)) {
      setPendingInsert({ kind: 'media', moduleId, blockType: blockType as 'video' | 'audio', afterBlockId })
      return
    }
    void addBlock(moduleId, blockType, afterBlockId)
  }

  const clearPendingInsert = () => setPendingInsert(null)

  const onEntryPickerConfirm = (entries: ContentEntry[]) => {
    const pending = pendingInsert
    clearPendingInsert()
    if (!pending || pending.kind !== 'entry' || entries.length === 0) return
    if (pending.multiple) {
      void addBlock(pending.moduleId, pending.blockType, pending.afterBlockId, {
        contentPatch: { item_ids: entries.map((e) => e.id) },
      })
      return
    }
    void addBlock(pending.moduleId, pending.blockType, pending.afterBlockId, {
      linkedEntryId: entries[0].id,
    })
  }

  // VISTOERA_GLOBAL_ICON_ACTION_POPOVER_V1 §13/§16 — same migration as
  // `deleteModule` above: mutation + undo/redo only, confirmation UI is the
  // anchored `VistoeraActionConfirm` wrapping the trigger button in JSX.
  const deleteBlockAction = async (block: GraphicPageBlock) => {
    const snapshot = block
    const holder = { id: block.id }
    const doDelete = async () => {
      await deleteGraphicBlock(workspaceId, siteId, pageId, holder.id)
      await refreshTree()
    }
    beginSave()
    try {
      await doDelete()
      endSave(true)
      pushHistory({
        label: '删除内容块',
        undo: async () => {
          const { data: nb } = await createGraphicBlock(workspaceId, siteId, pageId, snapshot.module_id, { block_type: snapshot.block_type })
          holder.id = nb.id
          await updateGraphicBlock(workspaceId, siteId, pageId, nb.id, {
            content: snapshot.content,
            is_visible: snapshot.is_visible,
            asset_id: snapshot.asset_id,
            linked_entry_id: snapshot.linked_entry_id,
          })
          await refreshTree()
        },
        redo: doDelete,
      })
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
      throw err
    }
  }

  const updateBlockField = async (
    block: GraphicPageBlock,
    patch: { content?: Record<string, unknown>; is_visible?: boolean; asset_id?: string | null; linked_entry_id?: string | null },
    historyLabel: string,
  ) => {
    const priorPatch: typeof patch = {}
    if (patch.content) priorPatch.content = block.content
    if (patch.is_visible !== undefined) priorPatch.is_visible = block.is_visible
    if (patch.asset_id !== undefined) priorPatch.asset_id = block.asset_id
    if (patch.linked_entry_id !== undefined) priorPatch.linked_entry_id = block.linked_entry_id
    const apply = async (p: typeof patch) => {
      beginSave()
      try {
        await updateGraphicBlock(workspaceId, siteId, pageId, block.id, p)
        await refreshTree()
        endSave(true)
      } catch (err) {
        endSave(false)
        message.error((err as SaasApiError).message)
      }
    }
    await apply(patch)
    pushHistory({ label: historyLabel, undo: () => apply(priorPatch), redo: () => apply(patch) })
  }

  const blockContentTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const onBlockContentChange = (block: GraphicPageBlock, patch: Record<string, unknown>) => {
    setTree((prev) =>
      prev
        ? {
            ...prev,
            modules: prev.modules.map((m) => ({
              ...m,
              blocks: m.blocks.map((b) => (b.id === block.id ? { ...b, content: { ...b.content, ...patch } } : b)),
            })),
          }
        : prev,
    )
    clearTimeout(blockContentTimers.current[block.id])
    markDebouncePending(`block:${block.id}`)
    blockContentTimers.current[block.id] = setTimeout(() => {
      clearDebouncePending(`block:${block.id}`)
      updateBlockField(block, { content: patch }, '编辑内容块')
    }, 700)
  }

  type BlockOrderEntry = { id: string; module_id: string }
  const applyBlockOrder = async (order: BlockOrderEntry[]) => {
    beginSave()
    try {
      await reorderGraphicBlocks(workspaceId, siteId, pageId, order)
      await refreshTree()
      endSave(true)
    } catch (err) {
      endSave(false)
      message.error((err as SaasApiError).message)
    }
  }
  const commitBlockReorder = async (nextOrder: BlockOrderEntry[], priorOrder: BlockOrderEntry[]) => {
    await applyBlockOrder(nextOrder)
    pushHistory({ label: '排序内容块', undo: () => applyBlockOrder(priorOrder), redo: () => applyBlockOrder(nextOrder) })
  }

  const moveBlock = async (module: GraphicPageModule, block: GraphicPageBlock, direction: -1 | 1) => {
    const ordered = module.blocks.slice().sort((a, b) => a.sort_order - b.sort_order)
    const idx = ordered.findIndex((b) => b.id === block.id)
    const swapWith = idx + direction
    if (swapWith < 0 || swapWith >= ordered.length) return
    const priorOrder = ordered.map((b) => ({ id: b.id, module_id: module.id }))
    const nextIds = ordered.map((b) => b.id)
    ;[nextIds[idx], nextIds[swapWith]] = [nextIds[swapWith], nextIds[idx]]
    const nextOrder = nextIds.map((id) => ({ id, module_id: module.id }))
    await commitBlockReorder(nextOrder, priorOrder)
  }

  // Block-list drag/sort — UI_V2 round: every module card now renders its
  // own block list simultaneously (not just the single active module), so
  // this can no longer be one `useEffect` keyed on `activeModuleId`. Each
  // module's `<div className="ge-block-list">` gets its own Sortable
  // instance via this callback ref, created on mount / destroyed on
  // unmount (module collapsed or removed) — same reorder→API→undo/redo
  // path as before, just multiplexed per module id.
  const moduleBlockSortables = useRef<Map<string, Sortable>>(new Map())
  const moduleBlockOrder = useRef<Map<string, string[]>>(new Map())
  const bindBlockSortable = (moduleId: string) => (el: HTMLDivElement | null) => {
    const existing = moduleBlockSortables.current.get(moduleId)
    if (existing) {
      existing.destroy()
      moduleBlockSortables.current.delete(moduleId)
    }
    if (!el) return
    const sortable = Sortable.create(el, {
      handle: '.ge-drag-handle',
      animation: 150,
      onEnd: (evt) => {
        if (evt.oldIndex == null || evt.newIndex == null || evt.oldIndex === evt.newIndex) return
        const priorIds = (moduleBlockOrder.current.get(moduleId) ?? []).slice()
        const nextIds = priorIds.slice()
        const [moved] = nextIds.splice(evt.oldIndex, 1)
        nextIds.splice(evt.newIndex, 0, moved)
        const priorOrder = priorIds.map((id) => ({ id, module_id: moduleId }))
        const nextOrder = nextIds.map((id) => ({ id, module_id: moduleId }))
        void commitBlockReorder(nextOrder, priorOrder)
      },
    })
    moduleBlockSortables.current.set(moduleId, sortable)
  }
  useEffect(
    () => () => {
      moduleBlockSortables.current.forEach((s) => s.destroy())
      moduleBlockSortables.current.clear()
    },
    [],
  )

  // Main-column module-card list drag/sort — same reorder action as the
  // sidebar navigator's Sortable below, just a second input surface bound
  // to the now-primary stacked-cards list (spec §六: drag reorder must
  // "真正工作" from wherever the module is actually visible).
  const moduleCardListRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = moduleCardListRef.current
    if (!el) return
    const sortable = Sortable.create(el, {
      handle: '.ge-drag-handle',
      animation: 150,
      onEnd: (evt) => {
        if (evt.oldIndex == null || evt.newIndex == null || evt.oldIndex === evt.newIndex) return
        const priorOrder = moduleOrderRef.current.slice()
        const nextOrder = priorOrder.slice()
        const [moved] = nextOrder.splice(evt.oldIndex, 1)
        nextOrder.splice(evt.newIndex, 0, moved)
        void commitModuleReorder(nextOrder, priorOrder)
      },
    })
    return () => sortable.destroy()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // -------------------------------------------------------------- Publish
  const onStatusAction = async (action: 'submit_review' | 'publish' | 'unpublish' | 'archive' | 'back_to_draft') => {
    setBusy(true)
    try {
      await changeGraphicPageStatus(workspaceId, siteId, pageId, action)
      await refreshTree()
      const labels: Record<string, string> = {
        submit_review: '已提交审核',
        publish: '已发布 — 新的公开快照已生效',
        unpublish: '已下架',
        archive: '已归档',
        back_to_draft: '已退回草稿',
      }
      message.success(labels[action])
    } catch (err) {
      message.error((err as SaasApiError).message || '操作失败')
    } finally {
      setBusy(false)
    }
  }

  // -------------------------------------------------------------- Preview
  const openPreview = async () => {
    setPreviewOpen(true)
    try {
      const { data } = await getGraphicPageDraftPreview(workspaceId, siteId, pageId)
      setPreviewTree(data)
    } catch (err) {
      message.error((err as SaasApiError).message || '预览加载失败')
    }
  }

  const editorImageResolver: ImageResolver = useMemo(
    () => ({
      block: (block) => (block.asset_id ? siteAssetVariantUrl(workspaceId, block.asset_id, 'web') : null),
      page: (p) => (p.cover_asset_id ? siteAssetVariantUrl(workspaceId, p.cover_asset_id, 'web') : null),
      entryCover: (entry) => (entry.cover_asset_id ? siteAssetVariantUrl(workspaceId, entry.cover_asset_id, 'web') : entry.cover_url ?? null),
    }),
    [workspaceId],
  )

  if (error) return <Alert type="error" message={error} showIcon style={{ margin: 24 }} />
  if (!tree || !page) return <Skeleton active paragraph={{ rows: 10 }} style={{ margin: 24 }} />

  const sortedModules = tree.modules.slice().sort((a, b) => a.sort_order - b.sort_order)
  moduleOrderRef.current = sortedModules.map((m) => m.id)
  const moduleThumbUrl = (m: GraphicPageModule): string | null => {
    const withAsset = m.blocks.find((b) => !!b.asset_id)
    return withAsset?.asset_id ? siteAssetVariantUrl(workspaceId, withAsset.asset_id, 'thumbnail') : null
  }
  const canUndo = undoStackRef.current.length > 0
  const canRedo = redoStackRef.current.length > 0
  void historyVersion

  const statusMenuItems = [
    page.status === 'draft' && { key: 'submit_review', label: '提交审核' },
    (page.status === 'draft' || page.status === 'in_review' || page.status === 'unpublished') && { key: 'publish', label: '发布' },
    page.status === 'published' && { key: 'unpublish', label: '下架' },
    page.status !== 'archived' && page.status !== 'draft' && { key: 'archive', label: '归档' },
    page.status !== 'draft' && { key: 'back_to_draft', label: '退回草稿' },
  ].filter(Boolean) as { key: string; label: string }[]

  return (
    <div className="ge-editor">
      <div className="ge-topbar">
        <span className="ge-topbar-back" onClick={() => navigate(`${siteBase}/graphic-pages`)}>
          <ArrowLeftOutlined /> 返回列表
        </span>
        <span className="ge-topbar-title" title={page.title}>{page.title || '未命名图文页面'}</span>
        <Tag>{GRAPHIC_PAGE_TYPE_LABELS[page.page_type]}</Tag>
        <Tag color={GRAPHIC_PAGE_STATUS_COLORS[page.status as GraphicPageStatus]}>{GRAPHIC_PAGE_STATUS_LABELS[page.status as GraphicPageStatus]}</Tag>
        <span className="ge-topbar-spacer" />
        <span className={`ge-save-state ${saveState === 'saving' ? 'is-saving' : saveState === 'error' ? 'is-error' : saveState === 'saved' ? 'is-saved' : ''}`}>
          {saveState === 'saving' && <><LoadingOutlined /> 保存中…</>}
          {saveState === 'saved' && lastSavedAt && <><CheckCircleFilled /> 已保存 {lastSavedAt.toLocaleTimeString()}</>}
          {saveState === 'error' && <><WarningFilled /> 保存失败</>}
          {saveState === 'idle' && '未保存修改'}
        </span>
        <button type="button" className="ge-btn ge-btn-icon" disabled={!canUndo} onClick={onUndo} title="撤销">
          <UndoOutlined />
        </button>
        <button type="button" className="ge-btn ge-btn-icon" disabled={!canRedo} onClick={onRedo} title="重做">
          <RedoOutlined />
        </button>
        <button type="button" className="ge-btn" onClick={() => { setPreviewDevice('mobile'); openPreview() }}>
          <MobileOutlined /> 手机预览
        </button>
        <button type="button" className="ge-btn" onClick={() => { setPreviewDevice('desktop'); openPreview() }}>
          <DesktopOutlined /> 电脑预览
        </button>
        {page.status === 'draft' || page.status === 'in_review' || page.status === 'unpublished' ? (
          <button type="button" className="ge-btn ge-btn-primary" disabled={busy} onClick={() => onStatusAction('publish')}>
            <CloudUploadOutlined /> 发布
          </button>
        ) : page.status === 'published' ? (
          <button type="button" className="ge-btn ge-btn-danger" disabled={busy} onClick={() => onStatusAction('unpublish')}>
            下架
          </button>
        ) : null}
        <Dropdown
          menu={{ items: statusMenuItems, onClick: ({ key }) => onStatusAction(key as never) }}
          trigger={['click']}
        >
          <button type="button" className="ge-btn ge-btn-icon">
            <MoreOutlined />
          </button>
        </Dropdown>
      </div>

      <div className="ge-body">
        <div className="ge-sidebar">
          <div className="ge-sidebar-card">
            <div className="ge-section-title" style={{ marginTop: 0 }}>封面（建议尺寸：1920 × 1080）</div>
            <ImageAssetPickerField workspaceId={workspaceId} siteId={siteId} assetId={page.cover_asset_id} onChange={onCoverAssetChange} purpose="site_cover" />
            {page.cover_asset_id && (
              <>
                <div style={{ marginTop: 10 }}>
                  <VisualCropEditor
                    label="封面桌面端构图"
                    imageUrl={siteAssetVariantUrl(workspaceId, page.cover_asset_id, 'web')}
                    value={page.cover_crop_desktop}
                    defaultValue={DEFAULT_CROP_DESKTOP}
                    aspectRatio={16 / 9}
                    onChange={(v) => onCoverCropChange('cover_crop_desktop', v)}
                  />
                </div>
                <div style={{ marginTop: 10 }}>
                  <VisualCropEditor
                    label="封面移动端构图"
                    imageUrl={siteAssetVariantUrl(workspaceId, page.cover_asset_id, 'web')}
                    value={page.cover_crop_mobile}
                    defaultValue={DEFAULT_CROP_MOBILE}
                    aspectRatio={3 / 4}
                    onChange={(v) => onCoverCropChange('cover_crop_mobile', v)}
                  />
                </div>
              </>
            )}
          </div>

          <div className="ge-section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            模块
            <button type="button" className="ge-btn ge-btn-icon" onClick={addModule} title="新增模块">
              <PlusOutlined />
            </button>
          </div>
          <div className="ge-module-list" ref={moduleListElRef}>
            {sortedModules.length === 0 && <Empty description="暂无模块" style={{ margin: '12px 0' }} />}
            {sortedModules.map((m, idx) => {
              const isActive = m.id === activeModuleId
              const moduleBlocks = m.blocks.slice().sort((a, b) => a.sort_order - b.sort_order)
              const thumb = moduleThumbUrl(m)
              const summaryText =
                moduleBlocks.length === 0
                  ? '暂无内容块'
                  : `${GRAPHIC_BLOCK_TYPE_LABELS[moduleBlocks[0].block_type]} · ${moduleBlocks.length} 个内容块`
              return (
                <div key={m.id} data-id={m.id}>
                  <div
                    className={`ge-module-item ${isActive ? 'is-active' : ''} ${!m.is_visible ? 'ge-module-item-hidden' : ''}`}
                    onClick={() => { setActiveModuleId(m.id); scrollToModule(m.id) }}
                  >
                    <span className="ge-drag-handle" title="拖动排序" onClick={(e) => e.stopPropagation()}>
                      <HolderOutlined />
                    </span>
                    {thumb && (
                      <span className="ge-list-cover" style={{ width: 32, height: 24 }}>
                        <img src={thumb} alt="" />
                      </span>
                    )}
                    <span className="ge-module-item-main">
                      <span className="ge-module-item-title">{m.title || '未命名模块'}</span>
                      <span className="ge-module-item-summary">{summaryText}</span>
                    </span>
                    <span className="ge-module-item-actions">
                      <button type="button" className="ge-icon-btn" disabled={idx === 0} onClick={(e) => { e.stopPropagation(); moveModule(m, -1) }} title="上移">
                        <UpOutlined />
                      </button>
                      <button
                        type="button"
                        className="ge-icon-btn"
                        disabled={idx === sortedModules.length - 1}
                        onClick={(e) => { e.stopPropagation(); moveModule(m, 1) }}
                        title="下移"
                      >
                        <DownOutlined />
                      </button>
                      <button type="button" className="ge-icon-btn" onClick={(e) => { e.stopPropagation(); duplicateModuleAction(m) }} title="复制">
                        <CopyOutlined />
                      </button>
                      <span onClick={(e) => e.stopPropagation()}>
                        <VistoeraActionConfirm
                          title={t('website:deleteModule.confirmTitle')}
                          description={t('website:deleteModule.confirmDescription', { name: m.title || t('website:untitledModule') })}
                          confirmLabel={t('actions:delete')}
                          cancelLabel={t('actions:cancel')}
                          onConfirm={() => deleteModule(m)}
                        >
                          <button type="button" className="ge-icon-btn is-danger" title={t('website:deleteImage.buttonTitle')}>
                            <DeleteOutlined />
                          </button>
                        </VistoeraActionConfirm>
                      </span>
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="ge-main">
          <div className="ge-card">
            <div className="ge-card-title is-collapsible" onClick={() => setBasicInfoCollapsed((v) => !v)}>
              基本信息
              <span className="ge-card-title-spacer" />
              <span className="ge-card-collapse-toggle">{basicInfoCollapsed ? <DownOutlined /> : <UpOutlined />}</span>
            </div>
            {!basicInfoCollapsed && (
              <div className="ge-basic-info-grid">
                <div className="ge-basic-info-col">
                  <div className="ge-field">
                    <label className="ge-field-label">标题 *</label>
                    <input
                      key={`page-title-${historyVersion}`}
                      type="text"
                      defaultValue={page.title}
                      onChange={(e) => onMetaFieldChange('title', e.target.value)}
                    />
                  </div>
                  <div className="ge-field-row">
                    <div className="ge-field">
                      <label className="ge-field-label">副标题（可选）</label>
                      <input
                        key={`page-subtitle-${historyVersion}`}
                        type="text"
                        defaultValue={page.subtitle}
                        onChange={(e) => onMetaFieldChange('subtitle', e.target.value)}
                      />
                    </div>
                    <div className="ge-field">
                      <label className="ge-field-label">作者（可选）</label>
                      <input
                        key={`page-author-${historyVersion}`}
                        type="text"
                        defaultValue={page.author}
                        onChange={(e) => onMetaFieldChange('author', e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ge-field">
                    <label className="ge-checkbox-label">
                      <input type="checkbox" checked={page.frontend_visible} onChange={(e) => onToggleFrontendVisible(e.target.checked)} />
                      前端展示（{page.frontend_visible ? '展示中' : '已隐藏'}）
                    </label>
                  </div>
                </div>
                <div className="ge-basic-info-col ge-basic-info-col-summary">
                  <div className="ge-field">
                    <label className="ge-field-label">{page.page_type === 'home' ? '首页欢迎语' : '摘要（可选）'}</label>
                    <textarea
                      key={`page-summary-${historyVersion}`}
                      rows={5}
                      maxLength={500}
                      defaultValue={page.summary}
                      onChange={(e) => onMetaFieldChange('summary', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="ge-module-card-list" ref={moduleCardListRef}>
            {sortedModules.length === 0 && <Empty description="暂无模块，点击左侧“+”新增" style={{ margin: '40px 0' }} />}
            {sortedModules.map((module, idx) => {
              const expanded = expandedModuleIds.has(module.id)
              const moduleBlocks = module.blocks.slice().sort((a, b) => a.sort_order - b.sort_order)
              moduleBlockOrder.current.set(module.id, moduleBlocks.map((b) => b.id))
              return (
                <div
                  key={module.id}
                  data-id={module.id}
                  className={`ge-module-card ${!module.is_visible ? 'ge-module-card-hidden' : ''}`}
                  ref={(el) => { moduleCardRefs.current[module.id] = el }}
                >
                  <div className="ge-module-card-header" onClick={() => toggleModuleExpanded(module.id)}>
                    <span className="ge-drag-handle" title="拖动排序" onClick={(e) => e.stopPropagation()}>
                      <HolderOutlined />
                    </span>
                    <span className="ge-module-badge">{idx + 1}</span>
                    <span className="ge-module-card-title">{module.title || '未命名模块'}</span>
                    <span className="ge-module-anchor">#{module.anchor_slug}</span>
                    <label className="ge-checkbox-label" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={module.is_visible}
                        onChange={(e) => toggleModuleField(module, 'is_visible', e.target.checked)}
                      />
                      前端可访问
                    </label>
                    <label className="ge-checkbox-label" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={module.show_in_toc}
                        onChange={(e) => toggleModuleField(module, 'show_in_toc', e.target.checked)}
                      />
                      显示在目录
                    </label>
                    <span className="ge-card-title-spacer" />
                    <button type="button" className="ge-icon-btn" onClick={(e) => { e.stopPropagation(); duplicateModuleAction(module) }} title="复制模块">
                      <CopyOutlined />
                    </button>
                    <span onClick={(e) => e.stopPropagation()}>
                      <VistoeraActionConfirm
                        title={t('website:deleteModule.confirmTitle')}
                        description={t('website:deleteModule.confirmDescription', { name: module.title || t('website:untitledModule') })}
                        confirmLabel={t('actions:delete')}
                        cancelLabel={t('actions:cancel')}
                        onConfirm={() => deleteModule(module)}
                      >
                        <button type="button" className="ge-icon-btn is-danger" title={t('website:deleteModule.buttonTitle')}>
                          <DeleteOutlined />
                        </button>
                      </VistoeraActionConfirm>
                    </span>
                    <button
                      type="button"
                      className="ge-icon-btn"
                      onClick={(e) => { e.stopPropagation(); toggleModuleExpanded(module.id) }}
                      title={expanded ? '折叠' : '展开'}
                    >
                      {expanded ? <UpOutlined /> : <DownOutlined />}
                    </button>
                  </div>

                  {expanded && (
                    <div className="ge-module-card-body">
                      <div className="ge-field-row ge-field-row-3">
                        <div className="ge-field">
                          <label className="ge-field-label">模块标题</label>
                          <input
                            key={`${module.id}-title-${historyVersion}`}
                            type="text"
                            defaultValue={module.title}
                            onChange={(e) => onModuleTextChange(module, 'title', e.target.value)}
                          />
                        </div>
                        <div className="ge-field">
                          <label className="ge-field-label">模块副标题（可选）</label>
                          <input
                            key={`${module.id}-subtitle-${historyVersion}`}
                            type="text"
                            defaultValue={module.subtitle}
                            onChange={(e) => onModuleTextChange(module, 'subtitle', e.target.value)}
                          />
                        </div>
                        <div className="ge-field">
                          <label className="ge-field-label">单元导语（可选）</label>
                          <input
                            key={`${module.id}-intro-${historyVersion}`}
                            type="text"
                            defaultValue={module.intro}
                            onChange={(e) => onModuleTextChange(module, 'intro', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="ge-block-list" ref={bindBlockSortable(module.id)}>
                        {moduleBlocks.map((block, bidx) => (
                          <div key={block.id} data-id={block.id}>
                            <div
                              className={`ge-block-card ${!block.is_visible ? 'is-hidden' : ''}`}
                              ref={(el) => { blockRefs.current[block.id] = el }}
                            >
                              <div className="ge-block-card-header">
                                <span className="ge-drag-handle" title="拖动排序">
                                  <HolderOutlined />
                                </span>
                                <span className="ge-block-card-type">{GRAPHIC_BLOCK_TYPE_LABELS[block.block_type]}</span>
                                <span className="ge-block-card-summary">{blockSummary(block)}</span>
                                <button
                                  type="button"
                                  className="ge-icon-btn"
                                  disabled={bidx === 0}
                                  onClick={() => moveBlock(module, block, -1)}
                                  title="上移"
                                >
                                  <UpOutlined />
                                </button>
                                <button
                                  type="button"
                                  className="ge-icon-btn"
                                  disabled={bidx === moduleBlocks.length - 1}
                                  onClick={() => moveBlock(module, block, 1)}
                                  title="下移"
                                >
                                  <DownOutlined />
                                </button>
                                <button
                                  type="button"
                                  className="ge-icon-btn"
                                  onClick={() => updateBlockField(block, { is_visible: !block.is_visible }, '切换可见性')}
                                  title="前端可见"
                                >
                                  {block.is_visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                </button>
                                <VistoeraActionConfirm
                                  title={t('website:deleteBlock.confirmTitle')}
                                  description={t('website:deleteBlock.confirmDescription')}
                                  confirmLabel={t('actions:delete')}
                                  cancelLabel={t('actions:cancel')}
                                  onConfirm={() => deleteBlockAction(block)}
                                >
                                  <button type="button" className="ge-icon-btn is-danger" title={t('website:deleteBlock.buttonTitle')}>
                                    <DeleteOutlined />
                                  </button>
                                </VistoeraActionConfirm>
                              </div>
                              <div className="ge-block-card-body">
                                <BlockEditPanel
                                  workspaceId={workspaceId}
                                  siteId={siteId}
                                  block={block}
                                  onContentChange={(patch) => onBlockContentChange(block, patch)}
                                  onAssetChange={(assetId) => updateBlockField(block, { asset_id: assetId }, '更换图片')}
                                  onLinkedEntryChange={(entryId) => updateBlockField(block, { linked_entry_id: entryId }, '更换关联内容')}
                                />
                              </div>
                            </div>
                            <BlockAddMenu compact onAdd={(type) => requestAddBlock(module.id, type, block.id)} />
                          </div>
                        ))}
                      </div>

                      <div className="ge-module-card-footer">
                        <BlockAddMenu onAdd={(type) => requestAddBlock(module.id, type)} />
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="ge-module-add-footer">
            <button type="button" className="ge-btn" onClick={addModule}>
              <PlusOutlined /> 新增模块
            </button>
          </div>
        </div>
      </div>

      <Drawer
        title={
          <Segmented
            value={previewDevice}
            onChange={(v) => setPreviewDevice(v as 'desktop' | 'mobile')}
            options={[
              { label: <><DesktopOutlined /> 电脑预览</>, value: 'desktop' },
              { label: <><MobileOutlined /> 手机预览</>, value: 'mobile' },
            ]}
          />
        }
        placement="right"
        width="min(100%, 1240px)"
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        destroyOnClose
      >
        <p style={{ color: 'var(--text-muted)', fontSize: 12.5, marginTop: -8, marginBottom: 12 }}>
          草稿预览 — 展示当前草稿数据（含前端可访问/可见过滤），并非已发布快照。
        </p>
        <div className="ge-preview-frame-wrap">
          <div className={`ge-preview-frame device-${previewDevice}`}>
            {previewTree ? (
              <GraphicTreeRenderer tree={previewTree} device={previewDevice} resolveImage={editorImageResolver} />
            ) : (
              <Skeleton active paragraph={{ rows: 8 }} style={{ padding: 24 }} />
            )}
          </div>
        </div>
      </Drawer>

      <ImageAssetPicker
        open={pendingInsert?.kind === 'image'}
        workspaceId={workspaceId}
        siteId={siteId}
        purpose="graphic_insert"
        title={pendingInsert?.blockType === 'hero' ? '选择 Hero 图片' : '选择图片'}
        selectionMode={pendingInsert?.blockType === 'image' ? 'multiple' : 'single'}
        onCancel={clearPendingInsert}
        onConfirm={(selections) => {
          const pending = pendingInsert
          clearPendingInsert()
          if (!pending || pending.kind !== 'image') return
          const assetIds = selections.map((s) => s.assetId).filter(Boolean)
          if (assetIds.length === 0) return
          void addImageBlocks(pending.moduleId, pending.blockType, pending.afterBlockId, assetIds)
        }}
      />

      {pendingInsert?.kind === 'entry' ? (
        <EntryPickerModal
          open
          workspaceId={workspaceId}
          siteId={siteId}
          contentType={pendingInsert.contentType}
          title={entryPickerTitle(pendingInsert.contentType, pendingInsert.multiple)}
          selectionMode={pendingInsert.multiple ? 'multiple' : 'single'}
          maxSelection={pendingInsert.multiple ? 24 : 1}
          onCancel={clearPendingInsert}
          onConfirm={onEntryPickerConfirm}
        />
      ) : null}

      <MediaUrlCreateDialog
        open={pendingInsert?.kind === 'media'}
        blockType={pendingInsert?.kind === 'media' ? pendingInsert.blockType : 'video'}
        onCancel={clearPendingInsert}
        onConfirm={(result) => {
          const pending = pendingInsert
          clearPendingInsert()
          if (!pending || pending.kind !== 'media') return
          void addBlock(pending.moduleId, pending.blockType, pending.afterBlockId, {
            contentPatch: { title: result.title, external_url: result.external_url },
          })
        }}
      />
    </div>
  )
}

/**
 * Block Picker — 1:1 ports LiangCollection's two-column block-type popup
 * (audit doc §17: grouped, two-per-row layout, not a plain 3-item
 * dropdown). Rendered both as the prominent "添加内容块" button at the
 * end of a module (`compact=false`) and as a slim inline "+" between
 * every existing block (`compact=true`, spec §16/§17's "插入式 block
 * picker" — inserting via `after_block_id` at any position, not just
 * appending to the end).
 */
function BlockAddMenu({ onAdd, compact }: { onAdd: (type: GraphicBlockType) => void; compact?: boolean }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={compact ? 'ge-block-insert-row' : 'ge-block-add-row'}>
      <Dropdown
        open={open}
        onOpenChange={setOpen}
        trigger={['click']}
        dropdownRender={() => (
          <div className="ge-block-add-menu" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, boxShadow: 'var(--shadow-card)' }}>
            {GRAPHIC_BLOCK_TYPE_GROUPS.map((group) => (
              <div key={group.title} style={{ width: '100%' }}>
                <div className="ge-block-add-group-title">{group.title}</div>
                <div className="ge-block-add-group-grid">
                  {group.types.map((t) => (
                    <button
                      key={t}
                      type="button"
                      className="ge-btn"
                      onClick={() => {
                        onAdd(t)
                        setOpen(false)
                      }}
                    >
                      {GRAPHIC_BLOCK_TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      >
        {compact ? (
          <button type="button" className="ge-block-insert-btn" title="在此处插入内容块">
            <PlusOutlined />
          </button>
        ) : (
          <button type="button" className="ge-btn ge-btn-primary">
            <PlusOutlined /> 添加内容块
          </button>
        )}
      </Dropdown>
    </div>
  )
}
